// « Séance » (#/seance): overview with four blocks (session hero, exercises, coach advice, feedback after the session) and a
// guided mode exercise by exercise (log load / reps, tick sets, previous / next, finish). Progress lives in the shared store, so
// Accueil and Bibliothèque show the same Commencer / Reprendre / Terminée, and the finished session reaches Mon suivi and the
// week rings. Appetite copycat world, authored motion scoped to .cc-seance. Videos: only qualified sources per exercise (video-fixtures.ts),
// otherwise « Vidéo de démonstration à venir ». Prescriptions and advice are demo values; the coach is real (official portrait), his assignment is a demo.
import { Button } from "@ubm/ui/components/button";
import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useDirectionalSwap } from "./motion-swap";
import { ArrowLeft, ArrowRight, BookOpen, CalendarDays, ChartLine, Check, ChevronLeft, ChevronRight, House, Minus, Plus, Store, Video } from "lucide-react";
import { StatusBar, type Route } from "./parts";
import { CustomSheet } from "./sheet";
import { store, useStore, type SeanceState, type SetLog } from "./store";
import { COACH as COACH0, COACH_ADVICE as COACH_ADVICE0, type SeanceExercise } from "./seance-fixtures";
import { CANONICAL_ID, CURRENT_WEEK, findSession, progState, sessionDef, useProgState } from "./programmations-state";
import { TEAM } from "./studio-fixtures";
import type { Session } from "./suivi-fixtures";
import type { CoachId } from "./messagerie-state";
import { messagerie } from "./messagerie-state";
import { exerciseVideo } from "./video-fixtures";
import { DemoVideo } from "./video-player";
import { ShellActions } from "./shell";
import { useShellImmersive } from "./shell-state";
import { DEMO_TIME, DEMO_TODAY } from "./demo-context";
import { healthGate, useHealth } from "./health-state";
import { HealthNotice } from "./questionnairesante";

interface SeanceProps { go: (route: Route) => void; onOutOfScope: (label: string) => void }
type Sheet = { kind: "exercise"; i: number } | { kind: "advice" };
const LEVELS = [{ v: "facile", l: "Facile" }, { v: "adaptee", l: "Adaptée" }, { v: "difficile", l: "Difficile" }] as const;
const rx = (e: SeanceExercise) => `${e.sets} × ${e.reps}${e.unit === "s" ? " s" : ""} · repos ${e.rest}`;
const freshLogs = (e: SeanceExercise): SetLog[] => Array.from({ length: e.sets }, () => ({ load: e.loaded ? e.previousLoad ?? 10 : 0, reps: e.reps, done: false }));

function SeanceSheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  // Escape, focus trap / return, inert background: the shared engine through CustomSheet (no window listener of its own).
  return <CustomSheet title={title} onClose={onClose} overlayClassName="cc-seance-overlay" className="cc-seance-sheet" escapeAllowed={() => !document.fullscreenElement}>
    {children}
    <p className="cc-home-demo">Prototype : prescription et conseils de démonstration, rien n'est envoyé.</p>
  </CustomSheet>;
}


// #/seance (canonical Séance A of the current week, unchanged) or #/seance?id=<session> (another session of a weekly programmation:
// same page, same player and videos, its own session-local state in programmations-state, never overwriting Séance A).
const readSid = () => window.location.hash.match(/[?&]id=([\w-]+)/)?.[1] ?? CANONICAL_ID;
export function Seance(props: SeanceProps) {
  const [sid, setSid] = useState(readSid);
  useEffect(() => { const on = () => setSid(readSid()); window.addEventListener("hashchange", on); return () => window.removeEventListener("hashchange", on); }, []);
  return <SeanceSession key={sid} sid={sid} {...props} />;
}

function SeanceSession({ go, sid }: SeanceProps & { sid: string }) {
  const shared = useStore();
  useProgState();
  const canonical = sid === CANONICAL_ID;
  const SEANCE = sessionDef(sid);
  const found = findSession(sid);
  const s = canonical ? shared.seance : progState.session(sid);
  const setS = (patch: Partial<SeanceState>) => canonical ? store.setSeance(patch) : progState.setSession(sid, patch);
  const doneId = canonical ? "seance-s3-A" : `seance-${sid}`;
  const complete = (session: Session) => { if (canonical) store.completeSeance(session); else { store.addDoneSession({ ...session, programmeSessionId: sid }); progState.setSession(sid, { done: true }); } };
  const coachId: CoachId = found?.p.coach ?? "alexandre";
  const team = TEAM[coachId === "antoine" ? 1 : 0];
  const COACH = canonical ? COACH0 : team ? { name: team.firstName, photo: team.photo, alt: team.alt } : null;
  const notes = canonical ? COACH_ADVICE0.full : [...(found?.p.notes ?? [])];
  const COACH_ADVICE = { preview: canonical ? COACH_ADVICE0.preview : notes[0] ?? "", full: notes };
  const backToWeek = () => { window.location.hash = `#/programmations?semaine=${found?.p.week ?? CURRENT_WEEK}`; };
  const ask = COACH && SEANCE ? { coachId, name: COACH.name, session: SEANCE.title.split(" · ")[0] ?? "Séance", href: canonical ? "#/seance" : `#/seance?id=${sid}` } : null;
  const [mode, setMode] = useState<"overview" | "workout">("overview");
  // Questionnaire santé (14/10): only under an explicit requireTraining configuration, start / resume show the review state and a
  // link to the health page instead of the CTA, and the guided mode is never entered (small prototype guard, not a security
  // boundary). A done session stays readable.
  const healthStatus = healthGate(useHealth(), "training");
  const workout = mode === "workout" && !healthStatus;
  useShellImmersive(workout); // guided player: focused view, no dock
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const commentId = useId(); // visible label of the coach comment (its former aria-label text)
  const dir = useRef(1);
  const stepRef = useRef<HTMLDivElement>(null);
  useDirectionalSwap(stepRef, `${mode}-${s.current}`, dir.current, { offset: 24, duration: 360, easing: "cubic-bezier(.22, 1, .36, 1)", reducedDuration: 120 });
  useEffect(() => { if (!toast) return; const t = window.setTimeout(() => setToast(null), 2200); return () => window.clearTimeout(t); }, [toast]);
  useEffect(() => { document.querySelector(".cc-seance .cc-scroll")?.scrollTo({ top: 0 }); }, [mode, s.current]);
  if (!SEANCE) return <div className="cc-screen cc-seance"><StatusBar /><div className="cc-scroll"><p className="cc-suivi-empty">Aucune séance prévue.</p></div></div>;

  const ex = SEANCE.exercises;
  const logsOf = (i: number) => s.logs[ex[i]!.name] ?? freshLogs(ex[i]!);
  const isDone = (i: number) => logsOf(i).every(x => x.done);
  const doneCount = ex.filter((_, i) => isDone(i)).length;
  const close = () => setSheet(null);

  const start = () => { if (healthStatus) return; setS({ started: true }); setMode("workout"); };
  const goTo = (i: number) => { dir.current = i >= s.current ? 1 : -1; setS({ current: i, started: true }); };
  const updateSet = (i: number, k: number, patch: Partial<SetLog>) => {
    const next = logsOf(i).map((x, j) => j === k ? { ...x, ...patch } : x);
    setS({ logs: { ...s.logs, [ex[i]!.name]: next } });
  };
  const finish = () => {
    const exercises = ex.map((e, i) => ({ e, done: logsOf(i).filter(x => x.done) })).filter(x => x.done.length)
      .map(({ e, done }) => ({ name: e.name, setLogs: done.map(d => ({ reps: d.reps, ...(e.loaded ? { load: d.load } : {}) })), ...(e.loaded ? { load: Math.max(...done.map(d => d.load)) } : {}), ...(e.unit === "s" ? { unit: "s" as const } : {}) }));
    complete({ id: doneId, date: DEMO_TODAY, time: DEMO_TIME, kind: "programme", title: SEANCE.title, minutes: SEANCE.minutes, exercises });
    setMode("overview"); setToast("Séance terminée · ajoutée à Mon suivi");
  };
  const sendFeedback = () => {
    setS({ feedback: { ...s.feedback, sent: true } });
    const prev = shared.doneSessions.find(x => x.id === doneId);
    if (prev) (canonical ? store.completeSeance : store.addDoneSession)({ ...prev, feeling: [LEVELS.find(l => l.v === s.feedback.level)?.l, s.feedback.comment.trim()].filter(Boolean).join(" · ") || undefined });
    setToast("Retour envoyé (démo)");
  };

  /* ---------------- Guided mode ---------------- */
  if (workout) {
    const i = s.current, e = ex[i]!, logs = logsOf(i), last = i === ex.length - 1;
    return <div className="cc-screen cc-seance">
      <StatusBar />
      <div className="cc-scroll">
        <header className="cc-seance-head">
          <button type="button" className="cc-icon-btn" aria-label="Vue d'ensemble de la séance" onClick={() => setMode("overview")}><ChevronLeft size={18} /></button>
          <div className="cc-seance-head-text"><p className="cc-seance-meta">Exercice {i + 1} sur {ex.length}{e.group ? ` · ${e.group}` : ""}</p><p className="cc-seance-head-title">{SEANCE.title}</p></div>
          <ShellActions compact />
        </header>
        <div className="cc-seance-progress" role="progressbar" aria-valuemin={0} aria-valuemax={ex.length} aria-valuenow={doneCount} aria-label="Exercices terminés"><i style={{ width: `${(doneCount / ex.length) * 100}%` }} /></div>

        <div ref={stepRef} className="cc-seance-step"><Fragment key={i}>
          <ExerciseVideo e={e} />
          <h1 className="cc-seance-title">{e.name}</h1>
          <p className="cc-seance-rx">{rx(e)}</p>
          <Button variant="ghost" size="sm" onClick={() => setSheet({ kind: "exercise", i })}>Consignes et démonstration <ChevronRight size={13} strokeWidth={2.4} /></Button>

          <ol className="cc-card cc-seance-sets">{logs.map((l, k) => <li key={k} data-done={l.done || undefined}>
            <span className="cc-seance-setn">Série {k + 1}</span>
            {e.loaded && <span className="cc-seance-step-field" aria-label={`Charge série ${k + 1}`}>
              <button type="button" aria-label="Moins 1 kg" onClick={() => updateSet(i, k, { load: Math.max(0, l.load - 1) })}><Minus size={14} /></button>
              <b>{l.load} kg</b>
              <button type="button" aria-label="Plus 1 kg" onClick={() => updateSet(i, k, { load: l.load + 1 })}><Plus size={14} /></button>
            </span>}
            <span className="cc-seance-step-field" aria-label={`${e.unit === "s" ? "Durée" : "Répétitions"} série ${k + 1}`}>
              <button type="button" aria-label={e.unit === "s" ? "Moins 5 s" : "Moins 1 répétition"} onClick={() => updateSet(i, k, { reps: Math.max(0, l.reps - (e.unit === "s" ? 5 : 1)) })}><Minus size={14} /></button>
              <b>{l.reps}{e.unit === "s" ? " s" : ""}</b>
              <button type="button" aria-label={e.unit === "s" ? "Plus 5 s" : "Plus 1 répétition"} onClick={() => updateSet(i, k, { reps: l.reps + (e.unit === "s" ? 5 : 1) })}><Plus size={14} /></button>
            </span>
            <button type="button" role="checkbox" aria-checked={l.done} aria-label={`Série ${k + 1} faite`} className="cc-seance-check" data-on={l.done || undefined} onClick={() => updateSet(i, k, { done: !l.done })}><Check size={15} strokeWidth={3} /></button>
          </li>)}</ol>
          {e.previousLoad !== undefined && <p className="cc-seance-meta">Dernière Séance A : {e.previousLoad} kg (Mon suivi).</p>}
        </Fragment></div>

        <nav className="cc-seance-nav" aria-label="Exercices">
          <button type="button" className="cc-seance-secondary" disabled={i === 0} onClick={() => goTo(i - 1)}><ArrowLeft size={16} /> Précédent</button>
          {last ? <Button variant="primary" size="lg" className="cc-cta cc-seance-cta" onClick={finish}><Check size={16} strokeWidth={2.6} /> Terminer la séance</Button>
            : <Button variant="primary" size="lg" className="cc-cta cc-seance-cta" onClick={() => goTo(i + 1)}>Suivant <ArrowRight size={16} /></Button>}
        </nav>
      </div>
      {sheet?.kind === "exercise" && <ExerciseSheet e={ex[sheet.i]!} onClose={close} ask={ask} />}
    </div>;
  }

  /* ---------------- Overview ---------------- */
  return <div className="cc-screen cc-seance">
    <StatusBar />
    <div className="cc-scroll">
      <header className="cc-account-head cc-seance-overhead">
        <button type="button" className="cc-icon-btn" aria-label="Retour au programme" onClick={backToWeek}><ChevronLeft size={18} strokeWidth={2} /></button>
        <ShellActions />
      </header>
      {/* 1 · Séance */}
      <section className="cc-card cc-seance-hero" aria-labelledby="se-title">
        <div className="cc-seance-hero-media">
          <img src={SEANCE.photo} alt="" />
          <span className="cc-seance-video-badge"><Video size={12} strokeWidth={2} /> Vidéo de démonstration à venir</span>
        </div>
        <div className="cc-seance-hero-body">
          <p className="cc-seance-meta">{SEANCE.programme} · {SEANCE.minutes} min · {ex.length} exercices</p>
          <h1 id="se-title" className="cc-seance-title">{SEANCE.title}</h1>
          {COACH && <p className="cc-seance-meta">Avec {COACH.name} · affectation de démo</p>}
          {(s.started || s.done) && <><div className="cc-seance-progress" aria-hidden="true"><i style={{ width: `${(doneCount / ex.length) * 100}%` }} /></div><p className="cc-seance-meta">{doneCount}/{ex.length} exercices terminés</p></>}
          {s.done ? <p className="cc-seance-ok"><Check size={15} strokeWidth={2.6} /> Séance terminée</p>
            : ex.length === 0 ? <p className="cc-seance-meta">Le détail de cette séance n'est pas encore partagé par ton coach.</p>
            : healthStatus ? <HealthNotice status={healthStatus} />
            : <Button variant="primary" size="lg" className="cc-cta cc-seance-cta" onClick={start}>{s.started ? `Reprendre · ${doneCount}/${ex.length}` : "Commencer"}</Button>}
        </div>
      </section>

      {/* 2 · Tes exercices */}
      <section className="cc-seance-block" aria-labelledby="se-ex">
        <div className="cc-sectionhead"><h2 id="se-ex">Tes exercices</h2></div>
        <ol className="cc-card cc-seance-list">{ex.map((e, i) => {
          const state = isDone(i) ? "done" : s.started && !s.done && i === s.current ? "active" : "todo";
          const groupStart = e.group && ex[i - 1]?.group !== e.group;
          return <li key={e.name}>
            {groupStart && <p className="cc-seance-group">{e.group} · enchaîne les exercices {i + 1} et {i + 2}</p>}
            <button type="button" className="cc-seance-row" data-state={state} data-grouped={e.group ? true : undefined} onClick={() => setSheet({ kind: "exercise", i })}>
              <img src={e.photo} alt="" />
              <span className="cc-suivi-row-text"><span className="cc-suivi-row-title">{e.name}</span><span className="cc-seance-meta">{rx(e)}</span></span>
              <span className="cc-seance-state" aria-label={state === "done" ? "Terminé" : state === "active" ? "En cours" : "À faire"}>{state === "done" ? <Check size={14} strokeWidth={3} /> : i + 1}</span>
            </button>
          </li>;
        })}</ol>
      </section>

      {/* 3 · Conseils du coach */}
      {COACH && COACH_ADVICE.full.length > 0 && <section className="cc-seance-block" aria-labelledby="se-coach">
        <div className="cc-sectionhead"><h2 id="se-coach">Conseils du coach</h2></div>
        <div className="cc-card cc-seance-coach">
          <img src={COACH.photo} alt={COACH.alt} />
          <div><p className="cc-suivi-row-title">{COACH.name}</p><p className="cc-seance-preview">{COACH_ADVICE.preview}</p>
            <p className="cc-seance-meta">Texte de démonstration, pas une citation.</p>
            <Button variant="ghost" size="sm" onClick={() => setSheet({ kind: "advice" })}>Voir les consignes <ChevronRight size={13} strokeWidth={2.4} /></Button></div>
        </div>
      </section>}

      {/* 4 · Retour après séance (only once finished) */}
      {s.done && <section className="cc-seance-block cc-seance-feedback" aria-labelledby="se-fb">
        <div className="cc-sectionhead"><h2 id="se-fb">Retour après séance</h2></div>
        <div className="cc-card cc-suivi-card cc-suivi-pad">
          {s.feedback.sent ? <>
            <p className="cc-seance-ok"><Check size={15} strokeWidth={2.6} /> Retour envoyé</p>
            <p className="cc-seance-meta">{LEVELS.find(l => l.v === s.feedback.level)?.l}{s.feedback.comment ? ` · « ${s.feedback.comment} »` : ""}</p>
            <Button variant="ghost" size="sm" onClick={() => setS({ feedback: { ...s.feedback, sent: false } })}>Modifier</Button>
          </> : <>
            <div className="cc-seg" role="radiogroup" aria-label="Ressenti de la séance">{LEVELS.map(l => <button key={l.v} type="button" role="radio" aria-checked={s.feedback.level === l.v} data-active={s.feedback.level === l.v || undefined} onClick={() => setS({ feedback: { ...s.feedback, level: l.v } })}>{l.l}</button>)}</div>
            <label className="cc-seance-comment-label" htmlFor={commentId}>Commentaire facultatif</label>
            <textarea id={commentId} className="cc-seance-comment" rows={3} placeholder="Un commentaire pour ton coach (facultatif)" value={s.feedback.comment} onChange={ev => setS({ feedback: { ...s.feedback, comment: ev.target.value } })} />
            <Button variant="primary" size="lg" className="cc-cta" disabled={!s.feedback.level} onClick={sendFeedback}>Envoyer mon retour</Button>
          </>}
        </div>
      </section>}
    </div>
    {toast && <div className="cc-seance-toast" role="status">{toast}</div>}
    {sheet?.kind === "exercise" && <ExerciseSheet e={ex[sheet.i]!} onClose={close} ask={ask} />}
    {sheet?.kind === "advice" && COACH && <SeanceSheet title="Consignes du coach" onClose={close}>
      <div className="cc-seance-coach-row"><img src={COACH.photo} alt={COACH.alt} /><span><b>{COACH.name}</b><small>Texte de démonstration, pas une citation.</small></span></div>
      <ul className="cc-seance-cues">{COACH_ADVICE.full.map(t => <li key={t}>{t}</li>)}</ul>
    </SeanceSheet>}
  </div>;
}

/** Exercise detail: the qualified demonstration of THIS exercise if one exists (video-fixtures), otherwise an honest empty state. */
/** Demonstration of THIS exercise (qualified source only), shown first in the guided step and in the detail sheet; otherwise
 *  an honest empty state. A variant note (e.g. barbell vs dumbbells) stays discreet under the player. */
function ExerciseVideo({ e }: { e: SeanceExercise }) {
  const demo = exerciseVideo(e.name);
  if (!demo) return <div className="cc-seance-video" role="note"><Video size={18} strokeWidth={1.8} /><span><b>Vidéo de démonstration à venir</b></span></div>;
  return <div className="cc-seance-demo">
    <DemoVideo video={demo} label={`Démonstration : ${e.name}`} />
    {demo.provider === "youtube" && demo.note && <p className="cc-seance-meta">{demo.note}</p>}
  </div>;
}

function ExerciseSheet({ e, onClose, ask }: { e: SeanceExercise; onClose: () => void; ask: { coachId: CoachId; name: string; session: string; href: string } | null }) {
  return <SeanceSheet title={e.name} onClose={onClose}>
    <ExerciseVideo e={e} />
    <p className="cc-seance-rx">{rx(e)}{e.group ? ` · ${e.group}` : ""}</p>
    <ol className="cc-seance-cues">{e.cues.map(c => <li key={c}>{c}</li>)}</ol>
    {e.previousLoad !== undefined && <p className="cc-seance-meta">Dernière Séance A : {e.previousLoad} kg.</p>}
    {/* Question to the session coach, with this exercise attached as context (removable before sending). */}
    {ask && <Button variant="ghost" size="sm" onClick={() => { messagerie.startWithContext(ask.coachId, { label: `À propos de ${e.name} · ${ask.session}`, href: ask.href }); window.location.hash = `#/messages?fil=${ask.coachId}`; }}>Poser une question à {ask.name.split(" ")[0]}</Button>}
  </SeanceSheet>;
}
